import type { AABB, PolygonWithHoles, Ring, Vec2 } from '../../geo/types';
import { ringBounds } from '../../geo/polygon';

type Inside = (p: Vec2) => boolean;
type Crossing = (a: Vec2, b: Vec2) => Vec2;

function againstX(x: number, keepAbove: boolean): readonly [Inside, Crossing] {
  return [
    (p) => (keepAbove ? p[0] >= x : p[0] <= x),
    (a, b) => [x, a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0])],
  ];
}

function againstY(y: number, keepAbove: boolean): readonly [Inside, Crossing] {
  return [
    (p) => (keepAbove ? p[1] >= y : p[1] <= y),
    (a, b) => [a[0] + ((b[0] - a[0]) * (y - a[1])) / (b[1] - a[1]), y],
  ];
}

// Sutherland-Hodgman against one side of the box.
function clipAgainst(ring: Ring, [inside, crossing]: readonly [Inside, Crossing]): Ring {
  const out: Vec2[] = [];
  let before = ring[ring.length - 1]!;
  let beforeIn = inside(before);
  for (const p of ring) {
    const pIn = inside(p);
    if (pIn !== beforeIn) out.push(crossing(before, p));
    if (pIn) out.push(p);
    [before, beforeIn] = [p, pIn];
  }
  return out;
}

function boxesMeet(a: AABB, b: AABB): boolean {
  return a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY;
}

function cropRing(ring: Ring, box: AABB): Ring | null {
  const bounds = ringBounds(ring);
  if (!boxesMeet(bounds, box)) return null;
  const within =
    bounds.minX >= box.minX &&
    bounds.maxX <= box.maxX &&
    bounds.minY >= box.minY &&
    bounds.maxY <= box.maxY;
  if (within) return ring;
  const cropped = [
    againstX(box.minX, true),
    againstX(box.maxX, false),
    againstY(box.minY, true),
    againstY(box.maxY, false),
  ].reduce<Ring>((r, side) => (r.length === 0 ? r : clipAgainst(r, side)), ring);
  return cropped.length >= 3 ? cropped : null;
}

/**
 * A polygon cut down to a box, for a boolean operation near the box to run on a few vertices
 * rather than on all of a large piece. Where the polygon leaves the box and comes back, the cut
 * runs along the box's edge and back, enclosing nothing there, which the boolean clears.
 */
export function cropToBox(polygon: PolygonWithHoles, box: AABB): PolygonWithHoles | null {
  const outer = cropRing(polygon.outer, box);
  if (outer === null) return null;
  const cropped = polygon.holes.map((hole) => cropRing(hole, box));
  const untouched =
    outer === polygon.outer && cropped.every((hole, k) => hole === polygon.holes[k]);
  if (untouched) return polygon;
  return { outer, holes: cropped.filter((hole): hole is Ring => hole !== null) };
}
